/*
    $3 or %1 of monthly average across that model if over $300
    6-2 FP
    2-10 FP
    10-6 FP

    $4
    OT4 FP
*/

import { DateTime } from "luxon";
import { Shift } from "../../schema";
import { SHIFT_CODES_KEYS } from "../../shiftCodes";
import { FP_OT4_PAY, FP_STANDARD_WEEKDAY_PAY, ShiftData, getMonthDateBoundaries, makeShiftData } from "../common";

export const getMonthlyAverageEarnings = async (allShifts: Shift[], model_id: string) => {
    const modelShifts = allShifts.filter((shift) => shift.model_id === model_id);

    if (modelShifts.length === 0) {
        console.log(`[FP Weekday Pay] No shifts found for model ${model_id}`);
        return 0;
    }

    const total_pay = modelShifts.reduce((total, shift) => {
        return total + shift.ppv_earned;
    }, 0);

    const average = total_pay / modelShifts.length;
    console.log(`[FP Weekday Pay] Model ${model_id} average earnings: $${average.toFixed(2)}`);
    return average;
}

export const getClampedHourlyPayBasedOnMonthlyAverageEarnings = async (allShifts: Shift[], model_id: string) => {
    const average = await getMonthlyAverageEarnings(allShifts, model_id);
    if (average <= 300) {
        console.log(`[FP Weekday Pay] Model ${model_id} using standard rate: $${FP_STANDARD_WEEKDAY_PAY}`);
        return FP_STANDARD_WEEKDAY_PAY;
    }
    const rate = average * 0.01;
    console.log(`[FP Weekday Pay] Model ${model_id} using percentage rate: $${rate.toFixed(2)}`);
    return rate;
}

export const calculateStandardWeekdayPayForShifts = async (allShifts: Shift[], standardShiftCodeIds: string[]): Promise<ShiftData[]> => {
    const weekdayShifts = allShifts.filter((shift) => {
        return standardShiftCodeIds.includes(shift.shift_code);
    });

    console.log(`[FP Weekday Pay] Found ${weekdayShifts.length} standard weekday shifts`);
    
    const shiftsByModel = weekdayShifts.reduce((acc, shift) => {
        if (!acc[shift.model_id]) {
            acc[shift.model_id] = [];
        }
        acc[shift.model_id].push(shift);
        return acc;
    }, {} as Record<string, Shift[]>);

    console.log(`[FP Weekday Pay] Processing shifts for ${Object.keys(shiftsByModel).length} models`);

    const allShiftData: ShiftData[] = [];
    
    for (const [model_id, modelShifts] of Object.entries(shiftsByModel)) {
        const hourlyPay = await getClampedHourlyPayBasedOnMonthlyAverageEarnings(modelShifts, model_id);
        
        const modelShiftData = modelShifts.map(shift => {
            const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
            const shift_pay = hours * hourlyPay;
            return makeShiftData(shift, shift_pay);
        });

        allShiftData.push(...modelShiftData);
        console.log(`[FP Weekday Pay] Model ${model_id} total standard shifts: ${modelShifts.length}`);
    }

    return allShiftData;
}

export const calculateOvertimeWeekdayPayForShifts = async (allShifts: Shift[], overtimeShiftCodeIds: string[]): Promise<ShiftData[]> => {
    const overtimeShifts = allShifts.filter((shift) => {
        return overtimeShiftCodeIds.includes(shift.shift_code);
    });

    console.log(`[FP Weekday Pay] Found ${overtimeShifts.length} overtime weekday shifts`);
    
    return overtimeShifts.map(shift => {
        const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
        const shift_pay = hours * FP_OT4_PAY;
        return makeShiftData(shift, shift_pay);
    });
}

export const weekdayPayCalculator = async (shifts: Shift[], month: number, year: number): Promise<ShiftData[]> => {
    console.log(`[FP Weekday Pay] Starting weekday pay calculation for ${month + 1}/${year}`);
    
    const standardShiftCodes = SHIFT_CODES_KEYS.filter(sc => ['6-2 FP', '2-10 FP', '10-6 FP'].includes(sc));
    const overtimeShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'OT4 FP');

    const { start, end } = getMonthDateBoundaries(DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }));

    const filteredShifts = shifts.filter(shift => 
        shift.start_date_time >= start && 
        shift.start_date_time < end
    );
    console.log(`[FP Weekday Pay] Found ${filteredShifts.length} total shifts for month`);

    const standardPay = await calculateStandardWeekdayPayForShifts(filteredShifts, standardShiftCodes);
    const overtimePay = await calculateOvertimeWeekdayPayForShifts(filteredShifts, overtimeShiftCodes);

    return [
        ...standardPay,
        ...overtimePay
    ];
}
